// FILE: src/pages/admin/AdminCurriculumDetailPage.jsx
// One curriculum version (WP-5; MTO Preface, CD2.4; matrix 0.7, 11.2, 11.3):
// status, MTO approval reference, totals, its lessons in course order, the
// last check against the MTO rules and the change log. Actions follow the
// status:
//   all       check · edit details (MTO approval reference; the name while DRAFT)
//   DRAFT     add a lesson · check · approve (MTO reference; refused with
//             CURRICULUM_INVALID / _LIMIT / _LOCKED, shown in words) · delete
//   APPROVED  make a new version (a draft copy) · check · retire
//   RETIRED   make a new version · check
//
//   GET    /api/admin/curricula/:id           + resourceList
//   POST   /api/admin/curricula/:id/versions  → 201 the new DRAFT
//   POST   /api/admin/curricula/:id/approve   { mtoApprovalRef }
//   POST   /api/admin/curricula/:id/retire
//   PATCH  /api/admin/curricula/:id           { name? (DRAFT only), mtoApprovalRef? }
//   DELETE /api/admin/curricula/:id           DRAFT only
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Link,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import fetchWithAuth from "../../utils/fetchWithAuth";
import { DetailList, EmptyState, ErrorState, LoadingState, PageHeader, Section } from "../../components/ui";
import { ResponsiveTable } from "../../components/admin/AdminBits";
import { ActionBar, ConfirmDialog, CurriculumStatusChip, PracticeChip } from "../../components/admin/ContentBits";
import usePermissions from "../../hooks/usePermissions";
import { LIMITS, MAX_APPROVED, curriculumName, passMarkText, problemList } from "../../components/admin/content";
import { errorPayload } from "../../components/admin/apiError";
import { formatDate, formatDateTime } from "../../components/admin/format";
import { LastCheck, minutesText } from "./AdminCurriculaPage";
import { CHECK_NAMES } from "./AdminCurriculumValidationPage";
import { kindText } from "../AdminResourceListPage";
import { formatDuration } from "../../utils/timeFormat";
import { errorCode, messageFor } from "../../i18n/errors";

const LIST = "/admin/curricula";
const CLOSED = { kind: null, busy: false, error: null };

/** The server's change-log line with lesson types in words ("Created CHAPTER 1 …" → "Created chapter 1 …"). */
export function logText(summary) {
  return String(summary || "")
    .replace(/\bCHAPTER\b/g, "chapter")
    .replace(/\bHOMELINK\b/g, "homelink")
    .replace(/\bmedia\b/g, "videos");
}

export default function AdminCurriculumDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState({ curriculum: null, loading: true, error: null });
  const [notice, setNotice] = useState(location.state?.notice || "");
  const [dialog, setDialog] = useState(CLOSED);
  const [approvalRef, setApprovalRef] = useState("");
  const [details, setDetails] = useState({ name: "", mtoApprovalRef: "" });
  const [detailErrors, setDetailErrors] = useState({});
  const [refError, setRefError] = useState("");
  const [versionError, setVersionError] = useState(null);
  const [making, setMaking] = useState(false);
  const latest = useRef(0);
  // RBAC: lessons need content.edit, curriculum changes curricula.manage,
  // approval curricula.approve; without them the actions aren't offered.
  const { can } = usePermissions();
  const canEditLessons = can("content.edit");
  const canManage = can("curricula.manage");
  const canApprove = can("curricula.approve");

  const load = useCallback(() => {
    const req = ++latest.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    fetchWithAuth(`/api/admin/curricula/${encodeURIComponent(id)}`)
      .then((curriculum) => {
        if (req === latest.current) setState({ curriculum, loading: false, error: null });
      })
      .catch((error) => {
        if (req === latest.current) setState({ curriculum: null, loading: false, error: error || "INTERNAL_ERROR" });
      });
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // A notice from another page (e.g. after "make a new version") belongs to this curriculum only.
  useEffect(() => {
    setNotice(location.state?.notice || "");
  }, [id, location.state]);

  const { curriculum: c, loading, error } = state;

  if (!c) {
    if (loading) return <LoadingState variant="page" label="Loading the curriculum…" />;
    const missing = ["CURRICULUM_NOT_FOUND", "NOT_FOUND"].includes(errorCode(error));
    return (
      <Box>
        <PageHeader title="Curriculum" breadcrumbs={[{ label: "Curricula", to: LIST }, { label: "Curriculum" }]} />
        {missing ? (
          <EmptyState
            icon={AccountTreeOutlinedIcon}
            title={messageFor("CURRICULUM_NOT_FOUND")}
            description="It may have been a draft that was deleted. Go back to the list and choose another."
            action={
              <Button component={RouterLink} to={LIST} variant="contained">
                Back to curricula
              </Button>
            }
          />
        ) : (
          <ErrorState error={error} title="We couldn't load this curriculum" onRetry={load} />
        )}
      </Box>
    );
  }

  const name = curriculumName(c);
  const lessons = Array.isArray(c.resourceList) ? c.resourceList : [];
  const titleOf = Object.fromEntries(lessons.map((r) => [r.id, r.title]));
  const draft = c.status === "DRAFT";
  const approved = c.status === "APPROVED";
  const log = [...(c.changeLog || [])].reverse();

  const open = (kind) => {
    setDialog({ kind, busy: false, error: null });
    if (kind === "approve") {
      setApprovalRef(c.mtoApprovalRef || "");
      setRefError("");
    }
    if (kind === "details") {
      setDetails({ name: c.name || "", mtoApprovalRef: c.mtoApprovalRef || "" });
      setDetailErrors({});
    }
  };
  const close = () => setDialog(CLOSED);

  const run = async (path, options, onDone) => {
    setDialog((d) => ({ ...d, busy: true, error: null }));
    try {
      const result = await fetchWithAuth(`/api/admin/curricula/${encodeURIComponent(c.id)}${path}`, options);
      setDialog(CLOSED);
      onDone(result);
    } catch (err) {
      setDialog((d) => ({ ...d, busy: false, error: err || "INTERNAL_ERROR" }));
    }
  };

  const approve = () => {
    const ref = approvalRef.trim();
    if (!ref) return setRefError("Enter the reference from the MTO approval.");
    if (ref.length > LIMITS.curriculumText) return setRefError(`Use 1 to ${LIMITS.curriculumText} characters.`);
    setRefError("");
    return run("/approve", { method: "POST", body: JSON.stringify({ mtoApprovalRef: ref }) }, () => {
      setNotice(`${name} is approved. New students are now enrolled on it, and its lessons are frozen.`);
      load();
    });
  };
  // The name while DRAFT (a new name restarts the version numbering); the MTO approval reference at any time.
  const saveDetails = () => {
    const found = {};
    const nameText = details.name.trim();
    const ref = details.mtoApprovalRef.trim();
    if (draft && !nameText) found.name = "Enter a name.";
    else if (nameText.length > LIMITS.curriculumText) found.name = `Use 1 to ${LIMITS.curriculumText} characters.`;
    if (!draft && !ref) found.mtoApprovalRef = "Enter the reference from the MTO approval.";
    else if (ref.length > LIMITS.curriculumText) found.mtoApprovalRef = `Use 1 to ${LIMITS.curriculumText} characters.`;
    setDetailErrors(found);
    if (Object.keys(found).length) return undefined;
    const body = {};
    if (draft && nameText !== c.name) body.name = nameText;
    if (ref !== (c.mtoApprovalRef || "")) body.mtoApprovalRef = ref || null;
    if (!Object.keys(body).length) return close();
    return run("", { method: "PATCH", body: JSON.stringify(body) }, () => {
      setNotice("The curriculum details were saved.");
      load();
    });
  };
  const detailProblems = dialog.kind === "details" && errorCode(dialog.error) === "VALIDATION_FAILED" ? problemList(errorPayload(dialog.error).problems) : [];
  const detailError = (field) => detailErrors[field] || detailProblems.find((p) => p.field === field)?.message || "";

  const retire = () =>
    run("/retire", { method: "POST" }, () => {
      setNotice(`${name} is retired. Students already on it finish it; new students get another approved curriculum.`);
      load();
    });
  const remove = () =>
    run("", { method: "DELETE" }, (result) => {
      const n = Number(result?.deletedResources) || 0;
      navigate(LIST, { state: { notice: `${name} was deleted${n ? `, with its ${n} ${n === 1 ? "lesson" : "lessons"}` : ""}.` } });
    });

  const newVersion = async () => {
    setMaking(true);
    setVersionError(null);
    try {
      const draftCopy = await fetchWithAuth(`/api/admin/curricula/${encodeURIComponent(c.id)}/versions`, { method: "POST" });
      navigate(`${LIST}/${draftCopy.id}`, {
        state: { notice: `${curriculumName(draftCopy)} was made as a draft copy of version ${c.version}. Change its lessons, then approve it.` }
      });
    } catch (err) {
      setVersionError(err || "INTERNAL_ERROR");
    } finally {
      setMaking(false);
    }
  };

  // CURRICULUM_INVALID carries the failed checks; name them.
  const approveDetail = (() => {
    if (!dialog.error) return null;
    const code = errorCode(dialog.error);
    if (code === "CURRICULUM_INVALID") {
      const checks = errorPayload(dialog.error).validation?.checks || {};
      const failing = Object.entries(checks)
        .filter(([, v]) => v?.status === "FAIL")
        .map(([k]) => CHECK_NAMES[k] || k);
      return (
        <>
          {failing.length > 0 && (
            <Box component="ul" sx={{ m: 0, mb: 1, pl: 2.5 }}>
              {failing.map((f) => (
                <li key={f}>{f}: fails</li>
              ))}
            </Box>
          )}
          <Link component={RouterLink} to={`${LIST}/${c.id}/validation`} color="inherit">
            See the check results
          </Link>
        </>
      );
    }
    if (code === "CURRICULUM_LIMIT") return `Approved now: ${MAX_APPROVED} of ${MAX_APPROVED}.`;
    return null;
  })();

  const hasActions = (draft && canEditLessons) || canManage || (draft && canApprove);
  const actions = (
    <>
      {draft && canEditLessons && (
        <Button component={RouterLink} to={`/admin/resources/new?curriculumId=${c.id}`} variant="outlined" startIcon={<AddIcon />}>
          Add a lesson
        </Button>
      )}
      {canManage && (
        <Button component={RouterLink} to={`${LIST}/${c.id}/validation`} variant="outlined" startIcon={<FactCheckOutlinedIcon />}>
          Check against MTO rules
        </Button>
      )}
      {canManage && (
        <Button variant="outlined" startIcon={<EditOutlinedIcon />} onClick={() => open("details")}>
          Edit details
        </Button>
      )}
      {draft && canApprove && (
        <Button variant="contained" startIcon={<VerifiedOutlinedIcon />} onClick={() => open("approve")}>
          Approve
        </Button>
      )}
      {!draft && canManage && (
        <Button variant="contained" startIcon={<ContentCopyOutlinedIcon />} onClick={newVersion} disabled={making}>
          {making ? "Making a new version…" : "Make a new version"}
        </Button>
      )}
      {approved && canManage && (
        <Button variant="outlined" startIcon={<Inventory2OutlinedIcon />} onClick={() => open("retire")}>
          Retire
        </Button>
      )}
      {draft && canManage && (
        <Button variant="outlined" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => open("delete")}>
          Delete draft
        </Button>
      )}
    </>
  );

  return (
    <Box>
      <PageHeader
        eyebrow="Curriculum"
        title={name}
        breadcrumbs={[{ label: "Curricula", to: LIST }, { label: name }]}
        description={
          draft
            ? "A draft: lessons can be added and changed. Check it against the MTO rules, then approve it with the MTO approval reference."
            : approved
              ? "Approved: its lessons are frozen exactly as MTO approved them. To change anything, make a new version."
              : "Retired: no new students start it. Students already on it finish it."
        }
      >
        <Box sx={{ mt: 1.5 }}>
          <CurriculumStatusChip status={c.status} />
        </Box>
        {hasActions && <ActionBar label="Curriculum actions">{actions}</ActionBar>}
        {draft && !canApprove && (
          <Typography sx={{ mt: 2, color: "text.secondary" }} data-testid="approve-not-allowed">
            Approving a curriculum needs the “Approve curricula” permission, which your role doesn't include. Ask an
            administrator to approve it.
          </Typography>
        )}
      </PageHeader>

      {notice && (
        <Alert severity="success" role="status" sx={{ mb: 2.5 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}
      {versionError && (
        <Alert severity="error" role="alert" sx={{ mb: 2.5 }} onClose={() => setVersionError(null)}>
          {messageFor(errorCode(versionError))}
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gap: 2.5,
          alignItems: "start",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.6fr) minmax(0, 1fr)" },
          mb: 2.5
        }}
      >
        <Section id="curriculum-summary" title="Summary">
          <DetailList
            items={[
              { label: "Status", value: <CurriculumStatusChip status={c.status} /> },
              { label: "MTO approval reference", value: c.mtoApprovalRef || "Not entered yet" },
              { label: "Approved", value: c.approvedAt ? formatDate(c.approvedAt) : "Not yet" },
              c.retiredAt ? { label: "Retired", value: formatDate(c.retiredAt) } : null,
              {
                label: "Previous version",
                value: c.previousVersionId ? (
                  <Link component={RouterLink} to={`${LIST}/${c.previousVersionId}`}>
                    Open the previous version
                  </Link>
                ) : (
                  "None: the first version"
                )
              },
              { label: "Lessons", value: String(lessons.length) },
              { label: "Core lessons (chapters)", value: minutesText(c.totalCoreMinutes) },
              { label: "Homelinks", value: minutesText(c.totalFlexMinutes) }
            ]}
          />
        </Section>
        <Section
          id="curriculum-check"
          title="Last MTO check"
          description="Hours of core content, an interactive activity and an assessment in every hour, video lengths and learning outcomes."
        >
          <LastCheck validation={c.validation} />
          {canManage && (
            <Button component={RouterLink} to={`${LIST}/${c.id}/validation`} variant="outlined" sx={{ mt: 2 }}>
              {c.validation?.checkedAt ? "Check again and see the results" : "Run the check"}
            </Button>
          )}
        </Section>
      </Box>

      <Stack spacing={2.5}>
        <Section
          id="curriculum-lessons"
          title="Lessons in course order"
          description="Chapters open one after another; each homelink opens when its parent chapter is finished."
          contentSx={{ p: { xs: 2, sm: 3 } }}
        >
          {lessons.length === 0 ? (
            <EmptyState
              compact
              title="No lessons yet"
              description={draft ? "Add the first chapter to this draft." : "This curriculum has no lessons."}
              action={
                draft && canEditLessons ? (
                  <Button component={RouterLink} to={`/admin/resources/new?curriculumId=${c.id}`} variant="contained">
                    Add a lesson
                  </Button>
                ) : null
              }
            />
          ) : (
            <ResponsiveTable label="Lessons in course order" minWidth={860}>
              <TableHead>
                <TableRow>
                  <TableCell component="th" scope="col" align="right">
                    Position
                  </TableCell>
                  <TableCell component="th" scope="col">
                    Title
                  </TableCell>
                  <TableCell component="th" scope="col">
                    Type
                  </TableCell>
                  <TableCell component="th" scope="col">
                    Parent chapter
                  </TableCell>
                  <TableCell component="th" scope="col">
                    Pass mark
                  </TableCell>
                  <TableCell component="th" scope="col" align="right">
                    Slides
                  </TableCell>
                  <TableCell component="th" scope="col" align="right">
                    Duration
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lessons.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell align="right">{r.order ?? "—"}</TableCell>
                    <TableCell component="th" scope="row">
                      <Link component={RouterLink} to={`/admin/resources/${r.id}`} sx={{ fontWeight: 600 }}>
                        {r.title}
                      </Link>
                      {r.isPractice && (
                        <Box sx={{ mt: 0.75 }}>
                          <PracticeChip />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>{kindText(r)}</TableCell>
                    <TableCell>{r.parentResourceId ? titleOf[r.parentResourceId] || "—" : "—"}</TableCell>
                    <TableCell>{r.isPractice ? "Not scored" : passMarkText(r.passMark)}</TableCell>
                    <TableCell align="right">{r.totalSlides || 0}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      {r.totalDuration ? formatDuration(r.totalDuration) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </ResponsiveTable>
          )}
        </Section>

        <Section id="curriculum-log" title="Change log" description="Every change to this version, newest first.">
          {log.length === 0 ? (
            <Typography sx={{ color: "text.secondary" }}>No changes recorded.</Typography>
          ) : (
            <Box component="ol" aria-label="Changes, newest first" sx={{ m: 0, pl: 2.5 }}>
              {log.map((e, i) => (
                <Typography component="li" key={`${e.at}-${i}`} sx={{ mb: 1 }}>
                  <Box component="span" sx={{ display: "block" }}>
                    {logText(e.summary)}
                  </Box>
                  <Box component="span" sx={{ display: "block", color: "text.secondary" }}>
                    {formatDateTime(e.at)}
                    {e.by ? "" : " · by a script (seed or migration)"}
                  </Box>
                </Typography>
              ))}
            </Box>
          )}
        </Section>
      </Stack>

      <ConfirmDialog
        id="approve-curriculum"
        open={dialog.kind === "approve"}
        icon={VerifiedOutlinedIcon}
        tone="primary"
        title={`Approve ${name}?`}
        description="Approving freezes every lesson exactly as it is now and records it as MTO-approved content. New students are enrolled on the newest approved curriculum. The curriculum must pass the MTO check first."
        confirmLabel="Approve"
        busyLabel="Approving…"
        busy={dialog.busy}
        error={dialog.error}
        errorDetail={approveDetail}
        onConfirm={approve}
        onClose={close}
      >
        <TextField
          id="approve-ref"
          label="MTO approval reference"
          value={approvalRef}
          onChange={(e) => {
            setApprovalRef(e.target.value);
            setRefError("");
          }}
          error={!!refError}
          helperText={refError || "As written on MTO's approval of this curriculum."}
          required
          fullWidth
          sx={{ mt: 1 }}
        />
      </ConfirmDialog>

      <ConfirmDialog
        id="curriculum-details"
        open={dialog.kind === "details"}
        icon={EditOutlinedIcon}
        tone="primary"
        title="Edit the curriculum details"
        description={
          draft
            ? "The name and the MTO approval reference. Versions are numbered per name, so a new name starts again at version 1."
            : "The MTO approval reference, as written on MTO's approval. The name of an approved or retired curriculum can't change."
        }
        confirmLabel="Save details"
        busyLabel="Saving…"
        busy={dialog.busy}
        error={dialog.error}
        onConfirm={saveDetails}
        onClose={close}
      >
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField
            id="details-name"
            label="Name"
            value={details.name}
            onChange={(e) => setDetails((d) => ({ ...d, name: e.target.value }))}
            disabled={!draft}
            error={!!detailError("name")}
            helperText={detailError("name") || (draft ? undefined : "Fixed once approved.")}
            required={draft}
            fullWidth
          />
          <TextField
            id="details-ref"
            label="MTO approval reference"
            value={details.mtoApprovalRef}
            onChange={(e) => setDetails((d) => ({ ...d, mtoApprovalRef: e.target.value }))}
            error={!!detailError("mtoApprovalRef")}
            helperText={detailError("mtoApprovalRef") || "As written on MTO's approval of this curriculum."}
            required={!draft}
            fullWidth
          />
        </Stack>
      </ConfirmDialog>

      <ConfirmDialog
        id="retire-curriculum"
        open={dialog.kind === "retire"}
        icon={Inventory2OutlinedIcon}
        tone="warning"
        title={`Retire ${name}?`}
        description="No new students will start this curriculum. Students already on it keep it and finish it."
        confirmLabel="Retire"
        busyLabel="Retiring…"
        busy={dialog.busy}
        error={dialog.error}
        onConfirm={retire}
        onClose={close}
      />

      <ConfirmDialog
        id="delete-curriculum"
        open={dialog.kind === "delete"}
        icon={DeleteOutlineIcon}
        tone="error"
        title={`Delete ${name}?`}
        description={`The draft and its ${lessons.length} ${lessons.length === 1 ? "lesson" : "lessons"} are deleted. This can't be undone.`}
        confirmLabel="Delete draft"
        busyLabel="Deleting…"
        confirmColor="error"
        busy={dialog.busy}
        error={dialog.error}
        onConfirm={remove}
        onClose={close}
      />
    </Box>
  );
}
